import ErrorState from "components/Error/ErrorState";
import LoadingIcon from "components/Loading/LoadingIcon";
import Table from "components/Table/Table";
import { OFFSET, PAGE_SIZE } from "constants/TableConstants";
import LienDirectorySearchBar from "pages/liens/components/LienDirectorySearchBar";
import { useLienDirectorySearch } from "pages/liens/hooks/useLienDirectorySearch";
import { useLiensData } from "pages/liens/hooks/useLiensData";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useGetLienDirectoryEntriesQuery } from "services/lien-api";

type LienDirectoryRow = {
  id: string;
  legalEntityName: string;
  marketingName: string;
  contractNumber: string;
  diagnosisCodes: string;
  planType: string;
  effectiveDate: string;
  parentOrganization: string;
  cmsRegion: string;
  subrogationContact: string;
  subrogationPhone: string;
  subrogationFax: string;
  notes: string;
  subrogationEmail: string;
  contactTitle: string;
  contactLastName: string;
  contactFirstName: string;
  contactPhone: string;
  contactAddress: string;
  contactCity: string;
  contactState: string;
  contactZip: string;
  lastUpdated: string;
};

const directoryColumns = [
  {
    key: "legalEntityName",
    name: "Legal Entity Name",
    width: 375,
  },
  {
    key: "marketingName",
    name: "Organization Marketing Name",
    width: 220,
  },
  {
    key: "contractNumber",
    name: "Contract Number",
    width: 150,
  },
  {
    key: "diagnosisCodes",
    name: "Diagnosis Codes",
    width: 150,
  },
  {
    key: "planType",
    name: "Plan Type",
    width: 200,
  },
  {
    key: "effectiveDate",
    name: "Contract Effective Date",
    width: 170,
  },
  {
    key: "parentOrganization",
    name: "Parent Organization",
    width: 250,
  },
  {
    key: "cmsRegion",
    name: "CMS Region Responsible",
    width: 200,
  },
  {
    key: "contactTitle",
    name: "Directory Contact Title",
    width: 200,
  },
  {
    key: "contactLastName",
    name: "Directory Last Name",
    width: 150,
  },
  {
    key: "contactFirstName",
    name: "Directory First Name",
    width: 150,
  },
  {
    key: "contactPhone",
    name: "Directory Phone",
    width: 150,
  },
  {
    key: "contactAddress",
    name: "Directory Address",
    width: 250,
  },
  {
    key: "contactCity",
    name: "Directory City",
    width: 150,
  },
  {
    key: "contactState",
    name: "Directory State",
    width: 100,
  },
  {
    key: "contactZip",
    name: "Directory Zip",
    width: 100,
  },
  {
    key: "subrogationContact",
    name: "Subrogation Contact",
    width: 200,
  },
  {
    key: "subrogationPhone",
    name: "Subrogation Phone",
    width: 150,
  },
  {
    key: "subrogationFax",
    name: "Subrogation Contact Fax",
    width: 150,
  },
  {
    key: "notes",
    name: "Allan Koba Notes",
    width: 200,
  },
  {
    key: "subrogationEmail",
    name: "Subrogation Email Address",
    width: 250,
  },
  {
    key: "lastUpdated",
    name: "Directory Last Updated",
    width: 150,
  },
] as const;

const LienDirectoryPage = () => {
  const { lienFileId } = useParams<{ lienFileId: string }>();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const fileId = params.get("file_id");
  const [currentPage, setCurrentPage] = useState(1);

  const { lienDirectorySearchTerm, setSearchTerm } = useLienDirectorySearch();

  // reset page to 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [lienDirectorySearchTerm]);

  // cleanup search term on unmount
  useEffect(() => {
    return () => {
      setSearchTerm("");
    };
  }, [setSearchTerm]);

  const { isLoading: liensLoading, error: liensError } = useLiensData({
    fileId,
  });

  const {
    data: directoryEntries,
    isLoading: directoryLoading,
    error: directoryError,
    isFetching: directoryFetching,
  } = useGetLienDirectoryEntriesQuery({
    limit: PAGE_SIZE,
    offset: OFFSET(currentPage),
    searchTerm: lienDirectorySearchTerm || undefined,
  });

  if (!lienFileId) {
    return <ErrorState />;
  }

  if (liensLoading || directoryLoading) {
    return <LoadingIcon />;
  }

  if (liensError || directoryError) {
    return <ErrorState />;
  }

  const directoryRows: LienDirectoryRow[] =
    directoryEntries?.results?.map((entry) => ({
      id: entry.id.toString(),
      legalEntityName: entry.legal_entity_name,
      marketingName: entry.organization_marketing_name || "",
      contractNumber: entry.contract_number,
      diagnosisCodes: entry.organization_type,
      planType: entry.plan_type,
      effectiveDate: entry.contract_effective_date || "",
      parentOrganization: entry.parent_organization || "",
      cmsRegion: entry.cms_region_responsible,
      subrogationContact: "",
      subrogationPhone: "",
      subrogationFax: "",
      notes: "",
      subrogationEmail: entry.directory_contact_email || "",
      contactTitle: entry.directory_contact_title || "",
      contactLastName:
        entry.directory_contact_name?.split(" ").slice(1).join(" ") || "",
      contactFirstName: entry.directory_contact_name?.split(" ")[0] || "",
      contactPhone: entry.directory_contact_phone || "",
      contactAddress: entry.directory_contact_street,
      contactCity: entry.directory_contact_city,
      contactState: entry.directory_contact_state,
      contactZip: entry.directory_contact_zip,
      lastUpdated: entry.directory_contact_last_update || "",
    })) || [];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="table-container-right">
      <div className="search-container">
        <LienDirectorySearchBar />
      </div>
      <Table<LienDirectoryRow>
        className="liens-directory mrg-top"
        columns={directoryColumns}
        rows={directoryRows}
        rowKeyGetter={(row) => row.id}
        onSelectedRowsChange={() => {}}
        isResizable
        isSortable
        isLoading={directoryFetching}
        pagination={{
          pageSize: PAGE_SIZE,
          pageIndex: currentPage,
          rowCount: directoryEntries?.count || 0,
          onPageChange: handlePageChange,
          isLoading: directoryLoading,
        }}
      />
    </div>
  );
};

export default LienDirectoryPage;
